// Stale code-split chunk recovery.
//
// After a redeploy the browser may still hold the previous build's index.html
// while the server serves a new build with different chunk hashes. A lazy route
// (`React.lazy(() => import('./pages/X'))`) then requests an old hashed chunk the
// new container no longer has → "Failed to fetch dynamically imported module".
// One full reload pulls the fresh index.html + current chunk hashes and resolves
// the mismatch. A sessionStorage flag prevents reload loops if the new build is
// genuinely broken (reload once, then surface the real error).
//
// This module is the single source of truth shared by main.tsx (window/Vite
// listeners) and ErrorBoundary (React-caught lazy-import rejections, which never
// surface as window errors).

const CHUNK_RELOAD_FLAG = 'ck:chunk-reload';

export function isStaleChunkError(message: unknown): boolean {
  if (typeof message !== 'string') return false;
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed')
  );
}

// True only the first time per session — lets callers decide whether a reload
// has already been attempted (so a still-failing chunk falls through to the
// real error path instead of looping).
export function hasReloadedForStaleChunk(): boolean {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_FLAG) !== null;
  } catch {
    return false;
  }
}

// Trigger one guarded full reload. No-op if a reload was already attempted this
// session. Returns true if it kicked off a reload, false if suppressed.
export function handleStaleChunk(): boolean {
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return false;
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
  } catch {
    /* sessionStorage unavailable — still attempt the reload below */
  }
  window.location.reload();
  return true;
}

// Clear the guard once the app has booted successfully, so a future stale chunk
// (e.g. after the next deploy) can trigger another reload.
export function clearStaleChunkFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}
